import { Component } from '@theme/component';
import { debounce, onDocumentLoaded } from '@theme/utilities';
import { MegaMenuHoverEvent } from '@theme/events';

const ACTIVATE_DELAY = 0;
const DEACTIVATE_DELAY = 350;

/**
 * Below this width the header menu becomes the mobile navigation row: a single
 * strip of links that scrolls left/right, rather than collapsing overflowing
 * items into the "More" item. Kept in sync with the matching media query in the
 * header section stylesheet.
 */
const SCROLL_MENU_QUERY = '(max-width: 749px)';

/**
 * A custom element that manages a header menu.
 *
 * @typedef {Object} State
 * @property {HTMLElement | null} activeItem - The currently active menu item.
 *
 * @typedef {object} Refs
 * @property {HTMLElement} overflowMenu - The overflow menu.
 * @property {HTMLElement[]} [submenu] - The submenu in each respective menu item.
 *
 * @extends {Component<Refs>}
 */
class HeaderMenu extends Component {
  requiredRefs = ['overflowMenu'];

  #abortController = new AbortController();

  connectedCallback() {
    super.connectedCallback();

    const { signal } = this.#abortController;

    this.overflowMenu?.addEventListener(
      'pointerleave',
      (event) => {
        if (this.#isEmulatedHover(event)) return;
        this.#debouncedDeactivate();
      },
      { signal }
    );

    // Touch support: the mega menu is hover driven, so without this a tap on a
    // parent link follows the link before the panel can be read. `pointerover`
    // is watched as well as `pointerdown` because it is the first event of a
    // tap — the pointer type has to be known before the `pointerenter` binding
    // on each item fires.
    this.addEventListener('pointerover', this.#recordPointerType, { capture: true, signal });
    this.addEventListener('pointerdown', this.#recordPointerType, { capture: true, signal });
    this.addEventListener('click', this.#handleClick, { capture: true, signal });
    document.addEventListener('pointerdown', this.#handleOutsidePointerDown, { signal });

    // Let the menu scroll sideways on phones rather than hiding most of it
    // behind "More".
    this.#scrollQuery.addEventListener('change', this.#syncOverflowMode, { signal });
    this.#syncOverflowMode();

    onDocumentLoaded(this.#preloadImages);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#abortController.abort();
  }

  /* ------------------------------------------------------------------ */
  /* Sideways scrolling vs. "More" overflow                              */
  /* ------------------------------------------------------------------ */

  #scrollQuery = matchMedia(SCROLL_MENU_QUERY);

  /**
   * Disable <overflow-list>'s collapsing behaviour on phones so every top-level
   * link stays in the strip. The accompanying CSS lets that strip scroll.
   */
  #syncOverflowMode = () => {
    const list = this.refs.overflowMenu;

    if (!(list instanceof HTMLElement)) return;

    if (this.#scrollQuery.matches) {
      list.setAttribute('disabled', 'true');
    } else {
      list.removeAttribute('disabled');
    }
  };

  /* ------------------------------------------------------------------ */
  /* Touch handling                                                      */
  /* ------------------------------------------------------------------ */

  /** @type {string} */
  #pointerType = 'mouse';

  /** @type {HTMLAnchorElement | null} */
  #tapOpenedLink = null;

  /** @param {PointerEvent} event */
  #recordPointerType = (event) => {
    this.#pointerType = event.pointerType || 'mouse';
  };

  /**
   * A tap emits a whole burst of pointer events — `pointerover`, `pointerenter`,
   * then `pointerout`/`pointerleave` the instant the finger lifts, all before
   * `click`. Fed into the hover bindings, that opens a panel and closes it again
   * in the same gesture. On touch we ignore them entirely and let `#handleClick`
   * drive the menu; mice are unaffected.
   * @param {Event} [event]
   */
  #isEmulatedHover(event) {
    if (this.#pointerType === 'mouse') return false;

    return typeof event?.type === 'string' && event.type.startsWith('pointer');
  }

  /**
   * On touch, the first tap on a link that owns a mega panel opens the panel;
   * tapping the same link again follows it.
   * @param {MouseEvent} event
   */
  #handleClick = (event) => {
    if (this.#pointerType === 'mouse') return;
    if (!(event.target instanceof Element)) return;

    const link = event.target.closest('a[aria-haspopup="true"]');

    if (!(link instanceof HTMLAnchorElement) || !this.contains(link)) return;

    // Second tap: let the browser follow the link.
    if (this.#tapOpenedLink === link) return;

    const item = link.closest('li');

    if (!item) return;

    event.preventDefault();
    this.#tapOpenedLink = link;
    this.activate(/** @type {any} */ ({ target: item }));
  };

  /**
   * Close an open panel when tapping anywhere outside the menu, since
   * `pointerleave` does not fire reliably for touch.
   * @param {PointerEvent} event
   */
  #handleOutsidePointerDown = (event) => {
    if ((event.pointerType || 'mouse') === 'mouse') return;
    if (event.target instanceof Node && this.contains(event.target)) return;
    if (!this.#state.activeItem) return;

    this.#deactivate();
  };

  /**
   * @type {State}
   */
  #state = {
    activeItem: null,
  };

  /**
   * Time to allow for a closing animation between initiating a deactivation and actually deactivating the active item.
   * @returns {number}
   */
  get animationDelay() {
    const value = this.dataset.animationDelay;
    return value ? parseInt(value, 10) : 0;
  }

  /**
   * Get the overflow menu
   */
  get overflowMenu() {
    return /** @type {HTMLElement | null} */ (this.refs.overflowMenu?.shadowRoot?.querySelector('[part="overflow"]'));
  }

  /**
   * Whether the overflow menu is hovered
   * @returns {boolean}
   */
  get overflowHovered() {
    return this.refs.overflowMenu?.matches(':hover') ?? false;
  }

  /**
   * Activate the selected menu item immediately
   * @param {PointerEvent | FocusEvent} event
   */
  activate = (event) => {
    if (this.#isEmulatedHover(event)) return;

    this.#debouncedDeactivate.cancel();
    this.#debouncedActivateHandler.cancel();

    this.#debouncedActivateHandler(event);
  };

  /**
   * Activate the selected menu item with a delay
   * @param {PointerEvent | FocusEvent} event
   */
  #activateHandler = (event) => {
    this.#debouncedDeactivate.cancel();

    this.dispatchEvent(new MegaMenuHoverEvent());

    this.removeAttribute('data-animating');

    if (!(event.target instanceof Element)) return;

    let item = findMenuItem(event.target);

    if (!item || item == this.#state.activeItem) return;

    const isDefaultSlot = event.target.slot === '';

    this.dataset.overflowExpanded = (!isDefaultSlot).toString();

    const previouslyActiveItem = this.#state.activeItem;

    if (previouslyActiveItem) {
      previouslyActiveItem.ariaExpanded = 'false';
    }

    this.#state.activeItem = item;
    this.ariaExpanded = 'true';
    item.ariaExpanded = 'true';

    let submenu = findSubmenu(item);

    if (!submenu && !isDefaultSlot) {
      submenu = this.overflowMenu;
    }

    // Mark submenu as active for content-visibility optimization
    if (submenu) {
      submenu.dataset.active = '';
    }

    const submenuHeight = submenu ? submenu.offsetHeight : 0;

    this.style.setProperty('--submenu-height', `${submenuHeight}px`);
    this.style.setProperty('--submenu-opacity', '1');
  };

  #debouncedActivateHandler = debounce(this.#activateHandler, ACTIVATE_DELAY);

  /**
   * Deactivate the active item after a delay
   * @param {PointerEvent | FocusEvent} event
   */
  deactivate(event) {
    if (this.#isEmulatedHover(event)) return;

    this.#debouncedActivateHandler.cancel();

    if (!(event.target instanceof Element)) return;

    const item = findMenuItem(event.target);

    // Make sure the item to be deactivated is still the active one. Ideally
    // we cancelled the debounce before the item was changed, but just in case.
    if (item === this.#state.activeItem) {
      this.#debouncedDeactivate();
    }
  }

  /**
   * Deactivate the active item immediately
   * @param {HTMLElement | null} [item]
   */
  #deactivate = (item = this.#state.activeItem) => {
    if (!item || item != this.#state.activeItem) return;
    // `:hover` sticks after a tap on touch devices, so only honour it for mice.
    if (this.#pointerType === 'mouse' && this.overflowHovered) return;

    this.#tapOpenedLink = null;

    this.style.setProperty('--submenu-height', '0px');
    this.style.setProperty('--submenu-opacity', '0');
    this.dataset.overflowExpanded = 'false';

    const submenu = findSubmenu(item);

    this.#state.activeItem = null;
    this.ariaExpanded = 'false';
    item.ariaExpanded = 'false';
    item.setAttribute('data-animating', '');

    setTimeout(() => {
      item.removeAttribute('data-animating');
      // Remove active state from submenu after animation completes
      if (submenu) {
        delete submenu.dataset.active;
      }
    }, Math.max(0, this.animationDelay - 150)); // Start header transition 150ms before submenu finishes
  };

  /**
   * Deactivate the active item after a delay
   * @param {PointerEvent | FocusEvent} event
   */
  #debouncedDeactivate = debounce(this.#deactivate, DEACTIVATE_DELAY);

  /**
   * Preload images that are set to load lazily.
   */
  #preloadImages = () => {
    const images = this.querySelectorAll('img[loading="lazy"]');
    images?.forEach((image) => image.removeAttribute('loading'));
  };
}

if (!customElements.get('header-menu')) {
  customElements.define('header-menu', HeaderMenu);
}

/**
 * Find the closest menu item.
 * @param {Element | null | undefined} element
 * @returns {HTMLElement | null}
 */
function findMenuItem(element) {
  if (!(element instanceof Element)) return null;

  if (element?.matches('[slot="more"')) {
    // Select the first overflowing menu item when hovering over the "More" item
    return findMenuItem(element.parentElement?.querySelector('[slot="overflow"]'));
  }

  return element?.querySelector('[ref="menuitem"]');
}

/**
 * Find the closest submenu.
 * @param {Element | null | undefined} element
 * @returns {HTMLElement | null}
 */
function findSubmenu(element) {
  const submenu = element?.parentElement?.querySelector('[ref="submenu[]"]');
  return submenu instanceof HTMLElement ? submenu : null;
}
